import { zodResolver } from "@hookform/resolvers/zod";
import { createContext, ReactNode, useContext, useState } from "react";
import {
  FormProvider,
  useForm,
  useFormContext,
  UseFormProps,
} from "react-hook-form";
import {
  PipelineConfigForm,
  PipelineConfigFormSchema,
} from "../../../schemas/doc_config";

interface ConfigModeContextType {
  isNew: boolean;
  isVisMode: boolean;
  setIsVisMode: React.Dispatch<React.SetStateAction<boolean>>;
}
type ConfigFormProps = Omit<UseFormProps<PipelineConfigForm>, "resolver">;

const ConfigModeContext = createContext<ConfigModeContextType | undefined>(
  undefined
);

interface ConfigFormProviderProps {
  children: ReactNode;
  isNew: boolean;
  formProps: ConfigFormProps;
}

export const ConfigFormProvider = ({
  children,
  isNew,
  formProps,
}: ConfigFormProviderProps) => {
  const [isEditMode, setIsEditMode] = useState(isNew);
  const [isVisMode, setIsVisMode] = useState(false);

  const methods = useForm<PipelineConfigForm>({
    ...formProps,
    resolver: zodResolver(PipelineConfigFormSchema),
  });

  const modeValue = {
    isNew,
    isEditMode,
    setIsEditMode,
    isVisMode,
    setIsVisMode,
  };

  return (
    <ConfigModeContext.Provider value={modeValue}>
      <FormProvider {...methods}>{children}</FormProvider>
    </ConfigModeContext.Provider>
  );
};

export const useConfigModeContext = () => {
  const context = useContext(ConfigModeContext);
  if (context === undefined) {
    throw new Error("useConfigMode must be used within a ConfigFormProvider");
  }
  return context;
};

export const useFormConfigContext = () => useFormContext<PipelineConfigForm>();
